import React, { useCallback, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import SaaSNotFoundState from "../../components/common/saas/SaaSNotFoundState";
import { showToast } from "../../components/common/notification/Notification";
import { getErrorText } from "../partnerNetwork/services/partnerNetworkService";
import { useListRecord } from "../partnerNetwork/hooks/useNetworkRecords";
import { useTeamService } from "./hooks/useTeamRecords";
import { TEAMS_HOME } from "./constants/teamEndpoints";
import "../partnerNetwork/partnerNetwork.css";

const FORM_ID = "team-form";
const MAX_DESCRIPTION_LENGTH = 500;

// Mobile's team sheet has the same three fields but no validation at all; a name and a seat count are the
// minimum for a usable team.
const validationSchema = Yup.object({
  name: Yup.string().trim().required("Team name is required"),
  description: Yup.string().trim().max(MAX_DESCRIPTION_LENGTH, `Description must be under ${MAX_DESCRIPTION_LENGTH} characters`),
  total_seats: Yup.number()
    .typeError("Total seats must be a number")
    .integer("Total seats must be a whole number")
    .min(1, "Total seats must be at least 1")
    .required("Total seats is required"),
});

/** Lives inside <Formik> so it can read the form state for the unsaved-changes warning. */
const TeamFormBody = ({ isEdit, saved, submitError }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      {submitError && (
        <div className="alert alert-danger pn-alert" role="alert">
          {submitError}
        </div>
      )}

      <Section title="Team details" hint={isEdit ? "Update the team's name, description and seats." : "Name your team and set how many seats it has."}>
        <div className="fk-grid">
          <TextField name="name" label="Team name" required placeholder="Enter team name" autoComplete="off" />
          <TextField name="total_seats" label="Total seats" required type="number" min="1" inputMode="numeric" placeholder="Enter total seats" autoComplete="off" />
          <TextAreaField
            name="description"
            label="Description"
            className="fk-col-full"
            rows={4}
            maxLength={MAX_DESCRIPTION_LENGTH}
            placeholder="What is this team for?"
          />
        </div>
      </Section>
    </Form>
  );
};

/** /partner/team/new and /partner/team/:Id/edit */
const TeamFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const seed = useLocation().state?.record;
  const exit = useExitWizard(TEAMS_HOME);
  const service = useTeamService();

  // No get-by-id endpoint: the team to edit comes out of the team list. Creating needs no list at all.
  const load = useCallback(() => (Id ? service.listTeams() : Promise.resolve([])), [Id, service]);
  const { record: team, loading, error, notFound, refetch } = useListRecord(load, Id, seed, "Unable to load this team.");

  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setSubmitError(null);
    const payload = { name: values.name.trim(), description: values.description.trim(), total_seats: values.total_seats };
    try {
      if (isEdit) await service.updateTeam(team, payload);
      else await service.createTeam(payload);
      showToast(isEdit ? "Team updated successfully" : "Team created successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(exit, 0);
    } catch (err) {
      // The form keeps its values so nothing is lost
      const text = getErrorText(err, "Unable to save team");
      setSubmitError(text);
      showToast(text, { type: "error" });
      setSubmitting(false);
    }
  };

  if (isEdit && error) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
              Retry
            </button>
            <Link to={TEAMS_HOME} className="btn btn-sm btn-outline-secondary">
              Back to Teams
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && notFound) {
    return <SaaSNotFoundState title="Team not found" description="This team could not be found. It may have been deleted." backLink={TEAMS_HOME} backLabel="Back to Teams" />;
  }

  const initialValues = {
    name: team?.name || "",
    description: team?.description || "",
    total_seats: team?.total_seats != null ? String(team.total_seats) : "",
  };

  return (
    <FormPageLayout
      title={isEdit ? "Edit Team" : "Create Team"}
      subtitle={isEdit ? "Update this team's details." : "Create a team to organise your members."}
      formId={FORM_ID}
      submitLabel={isEdit ? "Update Team" : "Create Team"}
      submitting={submitting}
      loading={isEdit && loading}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <TeamFormBody isEdit={isEdit} saved={saved} submitError={submitError} />
      </Formik>
    </FormPageLayout>
  );
};

export default TeamFormPage;
